
import { openDB } from 'idb';
import { TeacherDBSchema } from '../types';
import { TeacherNotification } from './types';

// Get or create the database instance
export const getDB = async () => {
  return openDB<TeacherDBSchema>('teacherDB', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('teachers')) {
        db.createObjectStore('teachers', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('notifications')) {
        const store = db.createObjectStore('notifications', { keyPath: 'id' });
        store.createIndex('byTeacherId', 'teacherId', { unique: false });
      }
    }
  });
};

// Initialize the notifications store
export const initNotificationsStore = async () => {
  const db = await getDB();
  return db;
};

// Add a notification for a teacher
export const addTeacherNotification = async (notification: TeacherNotification): Promise<string> => {
  try {
    const db = await initNotificationsStore();
    const notificationToAdd = {
      ...notification,
      date: notification.date || new Date().toISOString(),
      read: notification.read ?? false
    };
    
    const id = await db.add('notifications', notificationToAdd);
    return id as string;
  } catch (error) {
    console.error('Error adding teacher notification:', error);
    throw error;
  }
};

// Create a teacher notification (alias for addTeacherNotification)
export const createTeacherNotification = addTeacherNotification;

// Get all notifications for a specific teacher
export const getTeacherNotifications = async (teacherId?: string): Promise<TeacherNotification[]> => {
  try {
    const db = await initNotificationsStore();
    let notifications;
    
    if (teacherId) {
      const index = db.transaction('notifications').store.index('byTeacherId');
      notifications = await index.getAll(teacherId);
    } else {
      notifications = await db.getAll('notifications');
    }
    
    return notifications as unknown as TeacherNotification[];
  } catch (error) {
    console.error('Error getting teacher notifications:', error);
    return [];
  }
};

// Get a specific notification by ID
export const getNotificationById = async (id: string): Promise<TeacherNotification | null> => {
  try {
    const db = await initNotificationsStore();
    const notification = await db.get('notifications', id);
    return notification as unknown as TeacherNotification || null;
  } catch (error) {
    console.error('Error getting notification by ID:', error);
    return null;
  }
};

// Update a notification
export const updateNotification = async (id: string, updates: Partial<TeacherNotification>): Promise<boolean> => {
  try {
    const db = await initNotificationsStore();
    const notification = await getNotificationById(id);
    if (!notification) return false;
    
    const updatedNotification = { ...notification, ...updates };
    await db.put('notifications', updatedNotification);
    return true;
  } catch (error) {
    console.error('Error updating notification:', error);
    return false;
  }
};

// Mark a notification as read
export const markNotificationAsRead = async (id: string): Promise<boolean> => {
  return updateNotification(id, { isRead: true, read: true });
};

// Delete a notification
export const deleteNotification = async (id: string): Promise<boolean> => {
  try {
    const db = await initNotificationsStore();
    await db.delete('notifications', id);
    return true;
  } catch (error) {
    console.error('Error deleting notification:', error);
    return false;
  }
};

// Clear all notifications for a teacher
export const clearTeacherNotifications = async (teacherId: string): Promise<boolean> => {
  try {
    const db = await initNotificationsStore();
    const tx = db.transaction('notifications', 'readwrite');
    const index = tx.store.index('byTeacherId');
    let cursor = await index.openCursor(teacherId);
    
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    
    await tx.done;
    return true;
  } catch (error) {
    console.error('Error clearing teacher notifications:', error);
    return false;
  }
};
